import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSessions, getTerms, getClasses, getClassResults, getAnnualResults,
  getResultTemplates, exportResults, getResultSheet,
  holdResult, releaseResult, putResultHoldNote, computeAnnualResults,
  getFeeClearance, postFeeClearance,
} from '@/api/owner';
import {
  FileText, Download, Eye, Lock, Unlock, AlertCircle, CheckCircle2,
  Loader2, X, GraduationCap, ChevronDown, ChevronRight,
  Calendar, RefreshCw, DollarSign, Filter,
} from 'lucide-react';
import { resolveStorageUrl } from '@/utils/storage';
import { HoldNoteModal, type HoldMode } from '../components/HoldNoteModal';
import { openExternal } from '@/lib/native';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;

type Mode = 'termly' | 'annual';
type HeldFilter = 'all' | 'held' | 'not-held';

const statusTone = (status: string) => {
  const map: Record<string, string> = {
    draft:     'bg-slate-100 text-slate-700 border-slate-200',
    computed:  'bg-blue-50 text-blue-700 border-blue-200',
    approved:  'bg-violet-50 text-violet-700 border-violet-200',
    published: 'bg-green-50 text-green-700 border-green-200',
    held:      'bg-amber-50 text-amber-800 border-amber-200',
  };
  return map[status] ?? 'bg-slate-100 text-slate-600 border-slate-200';
};

export function ResultsPage() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('termly');
  const [sessionId, setSessionId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');
  const [classId, setClassId] = useState<number | ''>('');
  const [heldFilter, setHeldFilter] = useState<HeldFilter>('all');
  const [openSheet, setOpenSheet] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [holdBox, setHoldBox] = useState<{ mode: HoldMode; row: any } | null>(null);
  const [exportTemplateId, setExportTemplateId] = useState<number | ''>('');

  // ── Queries ──
  const sessionsQ = useQuery({ queryKey: ['owner-sessions'], queryFn: () => getSessions().then(unwrap) });
  const termsQ = useQuery({ queryKey: ['owner-terms'], queryFn: () => getTerms().then(unwrap) });
  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses({ per_page: 200 }).then(unwrap) });
  const templatesQ = useQuery({ queryKey: ['owner-result-templates'], queryFn: () => getResultTemplates().then(unwrap) });

  const sessions = arr<any>(sessionsQ.data);
  const terms = arr<any>(termsQ.data);
  const classes = arr<any>(classesQ.data);
  const templates = arr<any>(templatesQ.data);

  const visibleTerms = useMemo(
    () => sessionId ? terms.filter((t: any) => (t.session_id ?? t.academic_session_id) === Number(sessionId)) : terms,
    [terms, sessionId]
  );

  // Auto-pick current session
  useEffect(() => {
    if (!sessionId && sessions.length > 0) {
      const cur = sessions.find((s: any) => s.is_current) ?? sessions[0];
      setSessionId(cur.id);
    }
  }, [sessions, sessionId]);

  // Auto-pick current term (only in termly mode)
  useEffect(() => {
    if (mode !== 'termly') return;
    if (!termId && visibleTerms.length > 0) {
      const cur = visibleTerms.find((t: any) => t.is_current);
      if (cur) setTermId(cur.id);
    }
    if (termId && !visibleTerms.some((t: any) => t.id === termId)) setTermId('');
  }, [mode, visibleTerms, termId]);

  // Auto-pick first template
  useEffect(() => {
    if (!exportTemplateId && templates.length > 0) setExportTemplateId(templates[0].id);
  }, [templates, exportTemplateId]);

  const enabled = mode === 'annual' ? !!classId && !!sessionId : !!classId && !!termId;

  const termlyQ = useQuery({
    queryKey: ['results-class', classId, termId],
    queryFn: () => getClassResults(Number(classId), { term_id: Number(termId) }).then(unwrap),
    enabled: enabled && mode === 'termly',
  });
  const annualQ = useQuery({
    queryKey: ['results-annual', classId, sessionId],
    queryFn: () => getAnnualResults({ class_id: Number(classId), session_id: Number(sessionId) }).then(unwrap),
    enabled: enabled && mode === 'annual',
  });

  const termly = termlyQ.data;
  const annual = annualQ.data;
  const termlyRows = arr<any>(termly?.results);
  const annualRows = arr<any>(annual);

  const filteredTermly = useMemo(() => {
    if (heldFilter === 'all') return termlyRows;
    if (heldFilter === 'held') return termlyRows.filter((r: any) => r.held);
    return termlyRows.filter((r: any) => !r.held);
  }, [termlyRows, heldFilter]);

  // ── Mutations ──
  const holdM = useMutation({
    mutationFn: ({ resultId, reason, note }: { resultId: number; reason: 'unpaid_fees' | 'disciplinary' | 'manual'; note: string }) =>
      holdResult(resultId, { reason, note }),
    onSuccess: () => {
      setHoldBox(null); setNotice('Result held. The parent and student now see your note instead of the result.');
      qc.invalidateQueries({ queryKey: ['results-class', classId, termId] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Hold failed'),
  });

  const releaseM = useMutation({
    mutationFn: ({ resultId, note }: { resultId: number; note: string }) =>
      releaseResult(resultId, { note }),
    onSuccess: () => {
      setHoldBox(null); setNotice('Result released. The parent and student can see it now.');
      qc.invalidateQueries({ queryKey: ['results-class', classId, termId] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Release failed'),
  });

  const noteM = useMutation({
    mutationFn: ({ resultId, note }: { resultId: number; note: string }) => putResultHoldNote(resultId, { note }),
    onSuccess: () => { setHoldBox(null); setNotice('Hold note updated.'); qc.invalidateQueries({ queryKey: ['results-class', classId, termId] }); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Could not save the note'),
  });

  const computeAnnualM = useMutation({
    mutationFn: () => computeAnnualResults({
      class_id: Number(classId),
      session_id: Number(sessionId),
    }),
    onSuccess: (r: any) => {
      const d = unwrap(r);
      setNotice(`Annual computed for ${d?.students ?? 0} student(s).${d?.published ? ` ${d.published} published.` : ''}`);
      qc.invalidateQueries({ queryKey: ['results-annual', classId, sessionId] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Compute failed'),
  });

  const clearFeeM = useMutation({
    mutationFn: ({ studentId, note }: { studentId: number; note: string }) =>
      postFeeClearance(studentId, { term_id: Number(termId), note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['results-class', classId, termId] });
      qc.invalidateQueries({ queryKey: ['fee-clearance', classId, termId] });
      setHoldBox(null); setNotice('Fees marked as paid. If the result is still held, release it.');
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Fee clearance failed'),
  });

  // ── Export one student or whole class ──
  const exportOne = async (studentId: number) => {
    if (!exportTemplateId) { setError('Pick a template to export with.'); return; }
    try {
      const r: any = await exportResults({
        template_id: Number(exportTemplateId),
        student_id: studentId,
        term_id: Number(termId),
        type: 'student',
      });
      const d = unwrap(r);
      const path = d?.path;
      if (path) void openExternal(resolveStorageUrl(path));
      else setError('Export returned no path.');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Export failed');
    }
  };

  const exportClass = async () => {
    if (!exportTemplateId) { setError('Pick a template to export with.'); return; }
    if (!termId || !classId) { setError('Pick class + term first.'); return; }
    try {
      const r: any = await exportResults({
        template_id: Number(exportTemplateId),
        class_id: Number(classId),
        term_id: Number(termId),
        type: 'class',
      });
      const d = unwrap(r);
      const path = d?.path;
      if (path) void openExternal(resolveStorageUrl(path));
      else setError('Export returned no path.');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Export failed');
    }
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Assessments & Results</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Results</h1>
          <p className="text-slate-500 text-sm mt-1">
            View, hold/release and export published results
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => {
            if (mode === 'annual') annualQ.refetch();
            else termlyQ.refetch();
          }} disabled={!enabled}
            className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Mode + Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
        <div className="inline-flex bg-slate-100 rounded-lg p-1">
          <button onClick={() => setMode('termly')}
            className={`px-4 py-1.5 text-sm font-bold rounded-md transition ${
              mode === 'termly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
            }`}>
            <FileText className="w-3.5 h-3.5 inline mr-1" /> Termly
          </button>
          <button onClick={() => setMode('annual')}
            className={`px-4 py-1.5 text-sm font-bold rounded-md transition ${
              mode === 'annual' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
            }`}>
            <GraduationCap className="w-3.5 h-3.5 inline mr-1" /> Annual
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Session *</label>
            <select value={sessionId} onChange={(e) => { setSessionId(e.target.value ? Number(e.target.value) : ''); setTermId(''); }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose session…</option>
              {sessions.map((s: any) => (
                <option key={s.id} value={s.id}>{s.name}{s.is_current ? ' (active)' : ''}</option>
              ))}
            </select>
          </div>
          {mode === 'termly' && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Term *</label>
              <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')}
                disabled={!sessionId}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-50">
                <option value="">Choose term…</option>
                {visibleTerms.map((t: any) => <option key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Class *</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose class…</option>
              {classes.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}{c.arm ? ` · ${c.arm}` : ''}</option>
              ))}
            </select>
          </div>
        </div>

        {mode === 'termly' && enabled && (
          <div className="flex flex-wrap gap-3 items-center pt-2 border-t border-slate-100">
            <span className="text-xs font-semibold text-slate-600 inline-flex items-center gap-1">
              <Filter className="w-3 h-3" /> Show:
            </span>
            {(['all', 'held', 'not-held'] as HeldFilter[]).map((f) => (
              <button key={f} onClick={() => setHeldFilter(f)}
                className={`px-3 py-1 text-xs font-semibold rounded-full border transition ${
                  heldFilter === f
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'
                }`}>
                {f === 'all' ? 'All' : f === 'held' ? 'Held only' : 'Not held'}
              </button>
            ))}
          </div>
        )}
      </div>

      {notice && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-start gap-2">
          {notice}
          <button onClick={() => setNotice(null)} className="ml-auto text-green-700"><X className="w-4 h-4" /></button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Empty state */}
      {!enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {mode === 'annual'
            ? 'Pick a session and a class to view annual results.'
            : 'Pick a session, term, and class to view termly results.'}
        </div>
      )}

      {/* ─── TERMLY MODE ─── */}
      {mode === 'termly' && enabled && (
        termlyQ.isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto block" />
        ) : termlyQ.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
            Could not load results: {(termlyQ.error as any)?.response?.data?.message ?? 'Unknown error'}
          </div>
        ) : (
          <>
            {/* Summary banner */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <div className="flex flex-wrap justify-between items-center gap-2">
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    {termly?.class} · {termly?.term}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {termly?.students_in_class ?? 0} students in class ·{' '}
                    {termlyRows.length} result(s) ·{' '}
                    {arr<any>(termly?.without_result).length} missing
                    {termly?.broadsheet_status && ` · Broadsheet: ${termly.broadsheet_status}`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(termly?.counts ?? {}).map(([status, count]: [string, any]) => (
                    <span key={status} className={`px-2 py-0.5 rounded-full text-xs font-bold border ${statusTone(status)}`}>
                      {count} {status}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Export bar */}
            <ExportBar
              templates={templates}
              templateId={exportTemplateId}
              setTemplateId={setExportTemplateId}
              onExportClass={exportClass}
              count={termlyRows.length}
            />

            {/* Missing results warning */}
            {arr<any>(termly?.without_result).length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                <p className="font-semibold mb-1">
                  {arr<any>(termly?.without_result).length} student(s) in this class don't have a result yet
                </p>
                <p className="text-xs mb-1">Enter their scores in Score Entry, then compute results.</p>
                <details>
                  <summary className="cursor-pointer text-xs font-semibold underline">Show names</summary>
                  <ul className="mt-1 text-xs list-disc list-inside">
                    {arr<any>(termly?.without_result).map((s: any) => (
                      <li key={s.student_id}>{s.name}</li>
                    ))}
                  </ul>
                </details>
              </div>
            )}

            {/* Results table */}
            {filteredTermly.length === 0 ? (
              <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="font-semibold text-slate-700">
                  {heldFilter !== 'all' ? 'No results match this filter' : 'No results yet'}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {heldFilter !== 'all'
                    ? 'Try clearing the filter.'
                    : 'Enter scores in Score Entry, then compute results.'}
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-3 text-left w-10">#</th>
                      <th className="px-3 py-3 text-left">Student</th>
                      <th className="px-3 py-3 text-left">Adm. No.</th>
                      <th className="px-3 py-3 text-right">Total</th>
                      <th className="px-3 py-3 text-right">Average</th>
                      <th className="px-3 py-3 text-right">Position</th>
                      <th className="px-3 py-3 text-center">Grade</th>
                      <th className="px-3 py-3 text-left">Status</th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredTermly.map((r: any, idx: number) => (
                      <tr key={r.result_id} className={r.held ? 'bg-amber-50/40' : 'hover:bg-slate-50'}>
                        <td className="px-3 py-3 text-xs text-slate-500 font-mono">{idx + 1}</td>
                        <td className="px-3 py-3 font-medium text-slate-900">
                          {r.name}
                          {r.held && (
                            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded uppercase">
                              <Lock className="w-2.5 h-2.5" /> Held
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 font-mono text-xs text-slate-500">{r.admission_no}</td>
                        <td className="px-3 py-3 text-right font-mono">{r.total?.toFixed?.(1) ?? r.total ?? '—'}</td>
                        <td className="px-3 py-3 text-right font-mono">{r.average?.toFixed?.(1) ?? r.average ?? '—'}</td>
                        <td className="px-3 py-3 text-right font-mono">{r.position ?? '—'}</td>
                        <td className="px-3 py-3 text-center font-bold text-slate-900">{r.grade ?? '—'}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${statusTone(r.status)}`}>
                            {r.status}
                          </span>
                          {r.hold_reason && (
                            <p className="text-[10px] text-amber-700 mt-0.5">{String(r.hold_reason).replace('_', ' ')}</p>
                          )}
                          {r.hold_note && (
                            <button onClick={() => setHoldBox({ mode: 'note', row: r })} className="block text-left text-[11px] text-slate-600 mt-0.5 max-w-[14rem] hover:underline" title="Change note">“{r.hold_note}”</button>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="flex flex-wrap justify-end gap-1">
                            <button onClick={() => setOpenSheet(r.result_id)}
                              className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1"
                              title="View sheet">
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                            <button onClick={() => exportOne(r.student_id)}
                              className="text-xs font-semibold text-violet-600 hover:underline inline-flex items-center gap-1"
                              title="Export PDF">
                              <Download className="w-3.5 h-3.5" /> PDF
                            </button>
                            {r.held ? (
                              <button onClick={() => setHoldBox({ mode: 'release', row: r })} disabled={releaseM.isPending}
                                className="text-xs font-semibold text-green-700 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                                <Unlock className="w-3.5 h-3.5" /> Release
                              </button>
                            ) : (
                              <button onClick={() => setHoldBox({ mode: 'hold', row: r })} disabled={holdM.isPending}
                                className="text-xs font-semibold text-amber-700 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                                <Lock className="w-3.5 h-3.5" /> Hold
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Fee clearance widget for held results */}
            {heldFilter === 'held' && filteredTermly.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Held results need attention
                </h3>
                <p className="text-sm text-amber-800 mb-3">
                  If the parent has paid, mark fees cleared then release the hold. Two steps ensures the fee is recorded first.
                </p>
                <div className="space-y-2">
                  {filteredTermly.map((r: any) => (
                    <div key={r.result_id} className="flex flex-wrap items-center justify-between gap-2 bg-white p-3 rounded-lg border border-amber-200">
                      <div>
                        <p className="font-semibold text-slate-900">{r.name}</p>
                        <p className="text-xs text-slate-500">
                          {r.hold_note ?? (r.hold_reason ? String(r.hold_reason).replace('_', ' ') : 'No reason recorded')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setHoldBox({ mode: 'clear', row: r })} disabled={clearFeeM.isPending}
                          className="px-3 py-1.5 text-xs font-semibold text-amber-800 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 disabled:opacity-50">
                          1. Mark fees paid
                        </button>
                        <button onClick={() => setHoldBox({ mode: 'release', row: r })} disabled={releaseM.isPending}
                          className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">
                          2. Release hold
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )
      )}

      {/* ─── ANNUAL MODE ─── */}
      {mode === 'annual' && enabled && (
        annualQ.isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto block" />
        ) : annualQ.isError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
            Could not load annual results: {(annualQ.error as any)?.response?.data?.message ?? 'Unknown error'}
            <p className="text-xs mt-1">Annual results may not be computed yet.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap justify-between items-center gap-2">
              <div>
                <p className="text-sm font-bold text-slate-900">
                  {classes.find((c: any) => c.id === Number(classId))?.name ?? ''} · Annual
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {annualRows.length} student(s) with annual results
                </p>
              </div>
              <button onClick={async () => {
                if (await askConfirm('Compute annual results for this class and session? Existing annual rows will be overwritten.')) {
                  computeAnnualM.mutate();
                }
              }} disabled={computeAnnualM.isPending}
                className="px-3 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {computeAnnualM.isPending ? 'Computing…' : 'Compute annual'}
              </button>
            </div>

            {annualRows.length === 0 ? (
              <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
                <GraduationCap className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="font-semibold text-slate-700">No annual results yet</p>
                <p className="text-sm text-slate-500 mt-1">
                  Click "Compute annual" to aggregate the 3 terms into an annual position.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-3 text-left w-10">#</th>
                      <th className="px-3 py-3 text-left">Student</th>
                      <th className="px-3 py-3 text-left">Adm. No.</th>
                      <th className="px-3 py-3 text-right">Total</th>
                      <th className="px-3 py-3 text-right">Average</th>
                      <th className="px-3 py-3 text-right">Position</th>
                      <th className="px-3 py-3 text-center">Grade</th>
                      <th className="px-3 py-3 text-left">Promotion</th>
                      <th className="px-3 py-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {annualRows.map((r: any, idx: number) => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-3 py-3 text-xs text-slate-500 font-mono">{idx + 1}</td>
                        <td className="px-3 py-3 font-medium text-slate-900">{r.name}</td>
                        <td className="px-3 py-3 font-mono text-xs text-slate-500">{r.admission_no}</td>
                        <td className="px-3 py-3 text-right font-mono">{r.total?.toFixed?.(1) ?? r.total ?? '—'}</td>
                        <td className="px-3 py-3 text-right font-mono">{r.average?.toFixed?.(1) ?? r.average ?? '—'}</td>
                        <td className="px-3 py-3 text-right font-mono">{r.position ?? '—'}</td>
                        <td className="px-3 py-3 text-center font-bold text-slate-900">{r.grade ?? '—'}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${
                            r.promotion_status === 'promoted' ? 'bg-green-50 text-green-700 border-green-200' :
                            r.promotion_status === 'repeat'   ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            r.promotion_status === 'graduate' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            'bg-slate-100 text-slate-600 border-slate-200'
                          }`}>
                            {r.promotion_status ?? '—'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${statusTone(r.status)}`}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
              <b>Note:</b> Annual PDF export (termly + annual combined) is coming in a future round — currently
              each sheet is generated separately via the template system.
            </div>
          </>
        )
      )}

      {/* Sheet viewer modal */}
      {holdBox && (
        <HoldNoteModal mode={holdBox.mode} student={holdBox.row.name} currentNote={holdBox.row.hold_note} currentReason={holdBox.row.hold_reason}
          busy={holdM.isPending || releaseM.isPending || clearFeeM.isPending || noteM.isPending}
          onClose={() => setHoldBox(null)}
          onSubmit={({ reason, note }) => {
            const r = holdBox.row;
            if (holdBox.mode === 'hold') holdM.mutate({ resultId: r.result_id, reason, note });
            else if (holdBox.mode === 'release') releaseM.mutate({ resultId: r.result_id, note });
            else if (holdBox.mode === 'note') noteM.mutate({ resultId: r.result_id, note });
            else clearFeeM.mutate({ studentId: r.student_id, note });
          }} />
      )}
      {openSheet && (
        <SheetModal resultId={openSheet} onClose={() => setOpenSheet(null)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  EXPORT BAR
// ═══════════════════════════════════════════════════════════════
function ExportBar({ templates, templateId, setTemplateId, onExportClass, count }: {
  templates: any[];
  templateId: number | '';
  setTemplateId: (v: number | '') => void;
  onExportClass: () => void;
  count: number;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">PDF template</label>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[240px]">
            <option value="">Choose template…</option>
            {templates.map((t: any) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2 items-end">
          <span className="text-xs text-slate-500 self-center">One student: use PDF on their row.</span>
          <button onClick={onExportClass} disabled={!templateId || count === 0}
            className="px-3 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-1">
            <Download className="w-4 h-4" /> Export whole class ({count})
          </button>
        </div>
        {templates.length === 0 && (
          <p className="text-xs text-amber-700">
            No template exists — create one in Sheet Designer first.
          </p>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  SHEET VIEWER MODAL — uses getResultSheet data
// ═══════════════════════════════════════════════════════════════
function SheetModal({ resultId, onClose }: { resultId: number; onClose: () => void }) {
  const sheetQ = useQuery({
    queryKey: ['result-sheet', resultId],
    queryFn: () => getResultSheet(resultId).then(unwrap),
  });
  const s = sheetQ.data;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Result sheet</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 max-h-[80vh] overflow-y-auto">
            {sheetQ.isLoading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : sheetQ.isError ? (
              <p className="text-sm text-red-600">
                Could not load sheet: {(sheetQ.error as any)?.response?.data?.message ?? 'Unknown error'}
              </p>
            ) : !s ? (
              <p className="text-sm text-slate-500">No sheet data.</p>
            ) : (
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase">Student</p>
                  <p className="font-bold text-slate-900">{s.student?.name ?? '—'}</p>
                  <p className="text-xs text-slate-500">
                    {s.student?.admission_no ?? ''} · {s.class ?? ''} · {s.term ?? ''}
                  </p>
                </div>
                {Array.isArray(s.subjects) && s.subjects.length > 0 ? (
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700">
                        <th className="text-left p-2">Subject</th>
                        <th className="text-right p-2">Total</th>
                        <th className="text-center p-2">Grade</th>
                        <th className="text-right p-2">Position</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.subjects.map((sub: any, i: number) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="p-2">{sub.name ?? sub.subject_name ?? `Subject ${i + 1}`}</td>
                          <td className="p-2 text-right font-mono">{sub.total ?? '—'}</td>
                          <td className="p-2 text-center font-bold">{sub.grade ?? '—'}</td>
                          <td className="p-2 text-right font-mono">{sub.position ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-xs italic text-slate-500">
                    No subject breakdown available. This may be a held result or the sheet structure differs.
                  </p>
                )}
                {s.summary && (
                  <div className="flex flex-wrap gap-4 pt-2 border-t border-slate-200 text-xs">
                    {s.summary.total != null && <span><b>Total:</b> {s.summary.total}</span>}
                    {s.summary.average != null && <span><b>Average:</b> {Number(s.summary.average).toFixed(2)}</span>}
                    {s.summary.position != null && <span><b>Position:</b> {s.summary.position}</span>}
                    {s.summary.grade && <span><b>Grade:</b> {s.summary.grade}</span>}
                  </div>
                )}
                {s.hold && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                    <b>Held:</b> {s.hold.message ?? s.hold.reason}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
